// src/components/TextCompare/DiffView.tsx

import React, { useMemo, useRef, useEffect } from 'react';

interface Token {
    text: string;
    changeType: 'added' | 'deleted' | 'base' | 'modified' | 'different';
    highlighted?: boolean;
    diffIndexes?: number[];
}

interface MoveInfo {
    type: 'source' | 'target';
    fromLines?: [number, number];
    toLines?: [number, number];
    similarity: number;
}

interface ComparisonLine {
    changeType: 'added' | 'deleted' | 'base' | 'modified' | 'different';
    lineNumberA?: number;
    lineNumberB?: number;
    tokens1?: Token[];
    tokens2?: Token[];
    moveInfo?: MoveInfo;
}

interface DiffViewProps {
    comparison: ComparisonLine[] | null;
    scrollToIndex: { index: number | null } | null;
    viewType?: 'split' | 'unified';
    characterLevel?: boolean;
    wrapLines?: boolean;
}

const DiffView: React.FC<DiffViewProps> = ({
    comparison,
    scrollToIndex,
    viewType = 'split',
    characterLevel = false,
    wrapLines = true
}) => {
    const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Calculate dynamic width for line numbers
    const lnWidth = useMemo(() => {
        if (!comparison || comparison.length === 0) return 50;
        const maxLine = Math.max(
            ...comparison.slice(-5).map(l => Math.max(l.lineNumberA || 0, l.lineNumberB || 0)),
            comparison.length
        );
        const digits = String(maxLine).length;
        return Math.max(40, digits * 8 + 20); // 8px per digit + 20px padding
    }, [comparison]);

    // Navigation logic - using scrollIntoView for native scrolling
    useEffect(() => {
        const index = scrollToIndex?.index;
        if (index !== undefined && index !== null && rowRefs.current[index]) {
            rowRefs.current[index]?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [scrollToIndex]);

    const renderTokens = (tokens: Token[] | undefined, side: 'source' | 'target' | 'none' = 'none') => {
        if (!tokens) return null;
        return tokens.map((token, idx) => {
            const isSource = side === 'source';
            const isTarget = side === 'target';

            // Mimic original CSS: left side gets red highlights, right side gets green. Modified (inline differences) gets indigo.
            const highlightBg = isSource ? 'bg-[rgba(239,68,68,0.2)] text-red-900' :
                isTarget ? 'bg-[rgba(34,197,94,0.2)] text-green-900' :
                    'bg-[rgba(99,102,241,0.2)] text-indigo-900';

            if (characterLevel && token.diffIndexes && token.diffIndexes.length > 0) {
                const chars = (token.text || '').split('');
                return (
                    <span key={idx} className="inline-flex flex-wrap">
                        {chars.map((char, cIdx) => (
                            <span
                                key={cIdx}
                                className={`px-[0.5px] rounded-[2px] ${token.diffIndexes?.includes(cIdx) ? highlightBg : ''}`}
                            >
                                {char}
                            </span>
                        ))}
                    </span>
                );
            }

            return (
                <span
                    key={idx}
                    className={`inline-block rounded-[2px] ${token.highlighted ? highlightBg : ''}`}
                >
                    {token.text}
                </span>
            );
        });
    };

    const jumpToLine = (lineNumber: number, side: 'A' | 'B') => {
        if (!comparison) return;
        const markerIdx = comparison.findIndex(l =>
            side === 'A' ? l.lineNumberA === lineNumber : l.lineNumberB === lineNumber
        );
        if (markerIdx !== -1 && rowRefs.current[markerIdx]) {
            rowRefs.current[markerIdx]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const renderMoveBanner = (line: ComparisonLine, index: number) => {
        if (!line.moveInfo || !comparison) return null;

        // Show banner only for the first line of a moved block
        const showBanner = index === 0 ||
            !comparison[index - 1].moveInfo ||
            comparison[index - 1].moveInfo?.type !== line.moveInfo.type ||
            (line.moveInfo.type === 'source' && (comparison[index - 1].moveInfo?.toLines?.[0] !== line.moveInfo.toLines?.[0])) ||
            (line.moveInfo.type === 'target' && (comparison[index - 1].moveInfo?.fromLines?.[0] !== line.moveInfo.fromLines?.[0]));

        if (!showBanner) return null;

        const info = line.moveInfo;
        const lines = info.type === 'source' ? info.toLines : info.fromLines;
        if (!lines) return null;
        const linkText = lines[0] === lines[1] ? `line ${lines[0]}` : `lines ${lines[0]}-${lines[1]}`;

        const isSource = info.type === 'source';
        const bannerBg = isSource ? 'bg-[#fdf2f2]' : 'bg-[#f0fdf4]';
        const bannerBorder = isSource ? 'border-[#f87171]' : 'border-[#4ade80]';
        const linkColor = isSource ? 'text-[#ea580c]' : 'text-[#064e3b]';
        const badgeColor = isSource ? 'text-[#b91c1c]' : 'text-[#047857]';

        const BannerContent = () => (
            <div className={`mx-2 my-1 px-[12px] py-[4px] border border-dashed rounded-[6px] ${bannerBg} ${bannerBorder}`}>
                <div className="flex items-center gap-[12px] text-[0.8rem]">
                    <span
                        className={`font-semibold cursor-pointer ml-auto transition-all duration-200 hover:opacity-80 ${linkColor}`}
                        onClick={() => jumpToLine(lines[0], isSource ? 'B' : 'A')}
                        title="Jump to moved text"
                    >
                        {isSource ? 'Text moved to' : 'Text moved from'} {linkText}
                    </span>
                    <span className={`bg-[#ffffff] px-[6px] py-[2px] rounded-[4px] font-bold text-[0.7rem] ${badgeColor}`}>
                        ({info.similarity}% similarity)
                    </span>
                </div>
            </div>
        );

        if (viewType === 'split') {
            return (
                <div className="flex w-full select-none bg-white">
                    <div className="flex flex-1 min-w-0 relative border-r border-[#e2e8f0]">
                        {isSource ? (
                            <>
                                <div className="bg-slate-50 border-r border-slate-200" style={{ flex: `0 0 ${lnWidth}px` }}></div>
                                <div className="flex-1"><BannerContent /></div>
                            </>
                        ) : null}
                    </div>
                    <div className="flex flex-1 min-w-0 relative">
                        {!isSource ? (
                            <>
                                <div className="bg-slate-50 border-r border-slate-200" style={{ flex: `0 0 ${lnWidth}px` }}></div>
                                <div className="flex-1"><BannerContent /></div>
                            </>
                        ) : null}
                    </div>
                </div>
            );
        }

        // Unified View
        return (
            <div className="flex w-full select-none">
                <div className="bg-slate-50 border-r border-slate-200" style={{ flex: `0 0 ${lnWidth * 2}px` }}></div>
                <div className="flex-1">
                    <BannerContent />
                </div>
            </div>
        );
    };

    const renderRow = (line: ComparisonLine, index: number) => {
        const moveType = line.moveInfo?.type; // 'source' or 'target'
        const wrapClass = wrapLines ? 'whitespace-pre-wrap' : 'whitespace-pre';
        const lineContentClass = `px-3 font-['Inter',_'Segoe_UI',_sans-serif] text-[14px] leading-[24px] break-all w-full flex-1 ${wrapClass}`;

        const isMovedSource = moveType === 'source';
        const isMovedTarget = moveType === 'target';

        // Background classes based on changeType
        const getSideBg = (side: 'left' | 'right') => {
            let bgClass = 'bg-white';
            let bandClass = '';

            if (line.changeType === 'added' && side === 'right') {
                bgClass = 'bg-[rgba(34,197,94,0.12)]';
                bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#22c55e] after:z-10';
            } else if (line.changeType === 'deleted' && side === 'left') {
                bgClass = 'bg-[rgba(239,68,68,0.12)]';
                bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#ef4444] after:z-10';
            } else if (line.changeType === 'modified') {
                if (side === 'left') {
                    bgClass = 'bg-[rgba(239,68,68,0.08)]';
                    bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#6366f1] after:z-10';
                } else if (side === 'right') {
                    bgClass = 'bg-[rgba(34,197,94,0.08)]';
                    bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#6366f1] after:z-10';
                }
            } else if (line.changeType === 'different') {
                if (side === 'left') {
                    bgClass = 'bg-[rgba(239,68,68,0.12)]';
                    bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#ef4444] after:z-10';
                } else if (side === 'right') {
                    bgClass = 'bg-[rgba(34,197,94,0.12)]';
                    bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#22c55e] after:z-10';
                }
            }

            if (isMovedSource && side === 'left') {
                bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#f59e0b] after:z-[11]';
            } else if (isMovedTarget && side === 'right') {
                bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#f59e0b] after:z-[11]';
            } else if (isMovedSource && side === 'right') {
                bandClass = ''; // Suppress opposite side highlight for Moved rows
            } else if (isMovedTarget && side === 'left') {
                bandClass = ''; // Suppress opposite side highlight for Moved rows
            }

            return `${bgClass} ${bandClass}`.trim();
        };

        const getUnifiedRowBg = (type: 'added' | 'deleted' | 'modified-source' | 'modified-target' | 'base') => {
            let bgClass = 'bg-white';
            let bandClass = '';

            if (type === 'added') {
                bgClass = 'bg-[rgba(34,197,94,0.12)]';
                bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#22c55e] after:z-10';
            } else if (type === 'deleted') {
                bgClass = 'bg-[rgba(239,68,68,0.12)]';
                bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#ef4444] after:z-10';
            } else if (type === 'modified-source') {
                bgClass = 'bg-[rgba(239,68,68,0.08)]';
                bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#6366f1] after:z-10';
            } else if (type === 'modified-target') {
                bgClass = 'bg-[rgba(34,197,94,0.08)]';
                bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#6366f1] after:z-10';
            }

            if (isMovedSource && (type === 'deleted' || type === 'modified-source')) {
                bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#f59e0b] after:z-[11]';
            } else if (isMovedTarget && (type === 'added' || type === 'modified-target')) {
                bandClass = 'after:content-[\'\'] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-[#f59e0b] after:z-[11]';
            }

            return `relative ${bgClass} ${bandClass}`.trim();
        };

        const LineNumberCell = ({ num, isLeft, isEmpty = false }: { num?: number, isLeft?: boolean, isEmpty?: boolean }) => (
            <span
                className={`flex-shrink-0 text-right pr-2 font-['Inter',_'Segoe_UI',_sans-serif] text-[14px] leading-[24px] select-none border-slate-200 pt-[2px] bg-[#f8fafc] ${isEmpty ? 'text-transparent' : 'text-slate-400'} ${isLeft ? 'border-r' : ''}`}
                style={{ width: viewType === 'unified' ? '50%' : `${lnWidth}px` }}
            >
                {num || ''}
            </span>
        );


        if (viewType === 'unified') {
            const UnifiedRow = ({
                numA, numB, tokens, bgType, side
            }: {
                numA?: number, numB?: number, tokens?: Token[], bgType: any, side: 'source' | 'target' | 'none'
            }) => {
                const isEmptyContent = (!tokens || tokens.length === 0) || (tokens.length === 1 && !tokens[0].text);
                if (!numA && !numB && isEmptyContent) return null;

                return (
                    <div className={`flex w-full select-text min-h-[24px] ${getUnifiedRowBg(bgType)}`} ref={el => { if (bgType === 'added' || bgType === 'base' || bgType === 'modified-source' || bgType === 'deleted') rowRefs.current[index] = el; }}>
                        <div className="flex flex-shrink-0 select-none bg-slate-50 border-r border-slate-200" style={{ width: `${lnWidth * 2}px` }}>
                            <LineNumberCell num={numA} isLeft={true} isEmpty={!numA} />
                            <LineNumberCell num={numB} isLeft={false} isEmpty={!numB} />
                        </div>
                        <div className={lineContentClass}>{renderTokens(tokens, side)}</div>
                    </div>
                );
            };

            // Unified View - Modified Lines (Split into Source/Target)
            if (line.changeType === 'modified') {
                return (
                    <div key={index} className="relative block group border-b border-[#f9fafb]">
                        {renderMoveBanner(line, index)}
                        <UnifiedRow numA={line.lineNumberA} tokens={line.tokens1} bgType="modified-source" side="source" />
                        <UnifiedRow numB={line.lineNumberB} tokens={line.tokens2} bgType="modified-target" side="target" />
                    </div>
                );
            }

            // Unified View - Different Lines (Split into Deleted/Added)
            if (line.changeType === 'different') {
                return (
                    <div key={index} className="relative block group border-b border-[#f9fafb]">
                        {renderMoveBanner(line, index)}
                        <UnifiedRow numA={line.lineNumberA} tokens={line.tokens1} bgType="deleted" side="source" />
                        <UnifiedRow numB={line.lineNumberB} tokens={line.tokens2} bgType="added" side="target" />
                    </div>
                );
            }

            // Unified View - Standard Rows (Added, Deleted, Base)
            return (
                <div key={index} className="relative block group border-b border-[#f9fafb]">
                    {renderMoveBanner(line, index)}
                    <UnifiedRow
                        numA={line.lineNumberA}
                        numB={line.lineNumberB}
                        tokens={(line.changeType === 'added' || moveType === 'target') ? line.tokens2 : line.tokens1}
                        bgType={line.changeType as any}
                        side={(line.changeType === 'added' || moveType === 'target') ? 'target' : (line.changeType === 'deleted' ? 'source' : 'none')}
                    />
                </div>
            );
        }

        // Split View (Default)
        return (
            <div key={index} ref={el => { rowRefs.current[index] = el; }} className="relative block group border-b border-[#f9fafb]">
                {renderMoveBanner(line, index)}
                <div className="flex w-full select-text min-h-[24px]">
                    <div className={`flex flex-1 overflow-hidden relative border-r border-[#e2e8f0] ${getSideBg('left')}`}>
                        <LineNumberCell num={line.lineNumberA} isLeft={true} isEmpty={!line.lineNumberA} />
                        <div className={`overflow-x-auto ${lineContentClass}`}>{renderTokens(line.tokens1, 'source')}</div>
                    </div>
                    <div className={`flex flex-1 overflow-hidden relative ${getSideBg('right')}`}>
                        <LineNumberCell num={line.lineNumberB} isLeft={true} isEmpty={!line.lineNumberB} />
                        <div className={`overflow-x-auto ${lineContentClass}`}>{renderTokens(line.tokens2, 'target')}</div>
                    </div>
                </div>
            </div>
        );
    };

    if (!comparison || comparison.length === 0) {
        return (
            <div className="flex justify-center items-center min-h-[200px] text-slate-400 text-[15px] bg-slate-50 border border-dashed border-slate-300 rounded-lg m-6">
                <p>Compare some text to see the results here.</p>
            </div>
        );
    }

    return (
        <div className="bg-white border border-[#eef2f6] overflow-hidden shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] min-h-[400px]">
            <div className="w-full overflow-x-auto overflow-y-hidden">
                {comparison.map((line, index) => renderRow(line, index))}
            </div>
        </div>
    );
};

export default DiffView;
